"use client";

import React, { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { FileCheck2, Landmark, Compass, ArrowRight, Volume2, VolumeX } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
export default function Introduction() {
  const shouldReduceMotion = useReducedMotion();
  const [isMuted, setIsMuted] = useState(true);
  const playerRef = useRef<any>(null);
  useEffect(() => {
    // Load YouTube IFrame Player API script once
    if (typeof window !== "undefined" && !(window as any).YT) {
      const tag = document.createElement("script");
      tag.src = "https://www.youtube.com/iframe_api";
      const firstScriptTag = document.getElementsByTagName("script")[0];
      firstScriptTag?.parentNode?.insertBefore(tag, firstScriptTag);
    }
    const initPlayer = () => {
      if ((window as any).YT && (window as any).YT.Player) {
        playerRef.current = new (window as any).YT.Player("intro-video-frame", {
          events: {
            onReady: (event: any) => {
              if (event.target && typeof event.target.mute === "function") {
                event.target.mute();
              }
            }
          }
        });
      }
    };
    if ((window as any).YT && (window as any).YT.Player) {
      initPlayer();
    } else {
      const prevCallback = (window as any).onYouTubeIframeAPIReady;
      (window as any).onYouTubeIframeAPIReady = () => {
        if (typeof prevCallback === "function") {
          prevCallback();
        }
        initPlayer();
      };
    }
  }, []);
  const toggleMute = () => {
    if (isMuted) {
      if (playerRef.current && typeof playerRef.current.unMute === "function") {
        playerRef.current.unMute();
      }
      const iframe = document.getElementById("intro-video-frame") as HTMLIFrameElement | null;
      iframe?.contentWindow?.postMessage(JSON.stringify({
        event: "command",
        func: "unMute",
        args: []
      }), "*");
      setIsMuted(false);
    } else {
      if (playerRef.current && typeof playerRef.current.mute === "function") {
        playerRef.current.mute();
      }
      const iframe = document.getElementById("intro-video-frame") as HTMLIFrameElement | null;
      iframe?.contentWindow?.postMessage(JSON.stringify({
        event: "command",
        func: "mute",
        args: []
      }), "*");
      setIsMuted(true);
    }
  };
  const animationProps = shouldReduceMotion ? {
    initial: {
      opacity: 1
    },
    whileInView: {
      opacity: 1
    }
  } : {
    initial: {
      opacity: 0,
      y: 16
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true,
      amount: 0.25
    },
    transition: {
      duration: 0.38,
      ease: [0.16, 1, 0.3, 1]
    }
  };
  return <section id="giới-thiệu-ngắn-gọn" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Left Column: Institutional Text & Mandate */}
          <motion.div {...animationProps} className="lg:col-span-6 flex flex-col justify-center">
            {/* Eyebrow citation */}
            <div className="inline-flex items-center gap-2 mb-3">
              <span className="h-2 w-2 rounded-full bg-primary" />
              <span className="text-xs font-semibold tracking-wider uppercase text-muted-foreground">
                Tổ chức xã hội - nghề nghiệp toàn quốc
              </span>
            </div>

            {/* Verbatim Section Heading */}
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-secondary">
              Giới thiệu chung
            </h2>

            {/* Dual-bar Institutional Framing Divider */}
            <div className="mt-3 mb-6 relative w-full h-1 bg-border">
              <div className="absolute left-0 top-0 h-1 w-16 bg-secondary" />
            </div>

            {/* Main Overview Paragraphs */}
            <div className="space-y-4 text-base text-foreground/90 leading-relaxed font-sans">
              <p>
                Hội Địa lý môi trường Việt Nam (VAEG) là tổ chức xã hội - nghề
                nghiệp của công dân, tổ chức Việt Nam hoạt động trong lĩnh vực
                địa lý môi trường và các ngành khoa học liên quan trên phạm vi cả
                nước.
              </p>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Hội được thành lập và hoạt động hợp pháp theo Quyết định số
                483/QĐ-BNV ngày 02 tháng 04 năm 2021 của Bộ trưởng Bộ Nội vụ.
                Điều lệ chính thức của Hội được phê duyệt theo Quyết định số
                1157/QĐ-BNV ngày 03 tháng 11 năm 2021.
              </p>
            </div>

            {/* Key Official Mandates Strip */}
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div data-index={0} className="bg-card border border-border rounded-sm p-3.5 flex flex-col justify-between">
                <div className="flex items-center gap-2 mb-2 text-primary">
                  <Landmark className="size-4 shrink-0" />
                  <span className="text-xs font-semibold uppercase tracking-wide">
                    Pháp lý
                  </span>
                </div>
                <p className="text-xs text-muted-foreground font-medium">
                  Quyết định 483/QĐ-BNV do Bộ Nội vụ ban hành
                </p>
              </div>

              <div data-index={1} className="bg-card border border-border rounded-sm p-3.5 flex flex-col justify-between">
                <div className="flex items-center gap-2 mb-2 text-primary">
                  <FileCheck2 className="size-4 shrink-0" />
                  <span className="text-xs font-semibold uppercase tracking-wide">
                    Điều lệ
                  </span>
                </div>
                <p className="text-xs text-muted-foreground font-medium">
                  Quyết định 1157/QĐ-BNV phê duyệt điều lệ
                </p>
              </div>

              <div data-index={2} className="bg-card border border-border rounded-sm p-3.5 flex flex-col justify-between">
                <div className="flex items-center gap-2 mb-2 text-primary">
                  <Compass className="size-4 shrink-0" />
                  <span className="text-xs font-semibold uppercase tracking-wide">
                    Phạm vi
                  </span>
                </div>
                <p className="text-xs text-muted-foreground font-medium">
                  Nghiên cứu địa lý & môi trường toàn quốc
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Button asChild variant="default" size="default" className="rounded-sm font-medium">
                <Link to="/AboutPage" className="inline-flex items-center gap-2">
                  <span>Tìm hiểu về Hội</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="default" className="rounded-sm font-medium">
                <Link to="/OrganizationStructurePage">Cơ cấu tổ chức</Link>
              </Button>
            </div>
          </motion.div>

          {/* Right Column: Documentary Photograph Container */}
          <motion.div {...animationProps} transition={{
          duration: 0.38,
          delay: shouldReduceMotion ? 0 : 0.08,
          ease: [0.16, 1, 0.3, 1]
        }} className="lg:col-span-6">
            <div className="relative rounded-sm border border-border bg-card p-2 shadow-sm">
              <div className="relative aspect-[4/3] w-full overflow-hidden rounded-sm bg-muted">
                <iframe id="intro-video-frame" src="https://www.youtube.com/embed/AZ6ftDAx4QU?autoplay=1&mute=1&loop=1&playlist=AZ6ftDAx4QU&controls=1" title="Đại hội thành lập Hội Địa lý môi trường Việt Nam" className="h-full w-full absolute inset-0" frameBorder="0" allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen />
                <Button type="button" variant="ghost" size="icon" onClick={toggleMute} aria-label={isMuted ? "Bật âm thanh" : "Tắt âm thanh"} className="absolute top-3 right-3 z-10 bg-black/60 text-white rounded-full p-2 hover:bg-black/80 transition-colors">
                  {isMuted ? <VolumeX className="size-5" /> : <Volume2 className="size-5" />}
                </Button>
              </div>

              {/* Documentary Caption Bar */}
              <div className="px-3 pt-3 pb-1 flex flex-col sm:flex-row sm:items-center justify-between gap-1 text-xs text-muted-foreground border-t border-border mt-2">
                <span className="font-medium text-foreground">
                  Đại hội thành lập Hội Địa lý môi trường Việt Nam
                </span>
                <span className="italic">Hà Nội, Việt Nam</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}